import React, { Component } from 'react';
import DatePicker from 'react-date-picker';
import Select from 'react-select';
import moment from 'moment';
import { GetLocationRoutes } from '../libs/GraphQL';
import styledComponent from '../utils/styledComponent';
import Loader from '../components/atoms/Loader';
import StatusPill from '../components/atoms/StatusPill';

const StyledDiv = styledComponent('div', {
  '& .mainHeading': {
    color: '#000000',
    fontSize: '28px',
    fontWeight: 'normal',
    letterSpacing: '0.21px',
    marginTop: '24px',
  },
  '& .subHeading': {
    marginTop: '16px',
    fontSize: '18px',
    letterSpacing: '0.14px',
    color: '#000000',
    marginRight: '72px',
  },
  '& .wrap': {
    display: 'flex',
    alignItems: 'center',
  },
  '#DatePicker': {
    marginTop: '18px',
    marginRight: '15px',
    position: 'relative',
    zIndex: '100',
  },
  '#MarketPicker': {
    marginTop: '18px',
    marginRight: '15px',
    position: 'relative',
    zIndex: '100',
  },
  '#RouteTable': {
    marginTop: '48px',
    width: '100%',
    borderSpacing: '50px 12px',
    border: '1px solid black',
    '& thead': {
      th: {
        padding: '8px',
      },
    },
    'td, th': {
      padding: '4px 12px',
      border: '1px solid black',
      color: '#000000',
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
    th: {
      fontSize: '18px',
      textAlign: 'center',
      position: 'sticky',
      top: '0',
      zIndex: '1',
      background: '#ffffff',
    },
    td: {
      fontSize: '16px',
      opacity: '1',
      letterSpacing: '0.63px',
      height: '40px',

      '&.alignCenter': {
        textAlign: 'center',
      },
    },
    '& .header': {
      'td, th': {
        padding: '8px',
        fontSize: '20px',
        fontWeight: 'bold',
      },
    },
    '& .summary': {
      borderBottom: '2px solid black',
    },
  },
  '#EmptyRoutes': {
    marginTop: '24px',
    fontSize: '18px',
    backgroundColor: '#eeeeee',
    textAlign: 'center',
  },
  '#Loading': {
    padding: '32px',
    backgroundColor: '#ffffff',
  },
});

function getRouteColor(route) {
  const MAX_HUE_NUMBER = 360;
  const routeAsHashcode = route
    .split('')
    .reduce((hashCode, letter) => hashCode + letter.charCodeAt(0), 0);
  // This random number is used to make the color difference more distinct,
  // for silimar route names (e.g route_name1, route_name2, route_name3)
  const DISTINCTION_NUMBER = 22.5;

  return `hsl(${
    (routeAsHashcode * DISTINCTION_NUMBER) % MAX_HUE_NUMBER
  }, 100%, 85%)`;
}

export default class ViewWeeklyRoutes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      date: new Date(),
      locationRoutes: {
        routes: [],
      },
      sortedData: [],
      keptData: [],
      displayData: [],
      markets: [
        { value: 'ALL', label: 'ALL' },
        { value: 'MW', label: 'MW' },
        { value: 'NE', label: 'NE' },
        { value: 'WC', label: 'WC' },
        { value: 'S', label: 'S' },
      ],
      selectedMarket: { value: 'ALL', label: 'ALL' },
      isLoading: false,
    };
    this.getLocationRoutes = this.getLocationRoutes.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.appendZeroToFormat = this.appendZeroToFormat.bind(this);
    this.formatDateObject = this.formatDateObject.bind(this);
  }

  async componentDidMount() {
    try {
      this.getLocationRoutes(this.state.date);
    } catch (e) {
      console.error(e);
    }
  }

  appendZeroToFormat(val) {
    /*
            Takes date / month as input and appends 0 to it if value is < 10
            Example: if date is 9, this method returns '09'

            @param val: int
            return: String
        */
    if (val < 10) return `0${val.toString()}`;
    return val.toString();
  }

  formatDateObject(date) {
    /*
            Takes Date object as input and formats it to "YYYY-mm-dd" string

            @param date: Date.prototype
            return: String
        */
    let dateString = '';
    dateString += `${date.getFullYear().toString()}-${this.appendZeroToFormat(
      date.getMonth() + 1,
    )}-${this.appendZeroToFormat(date.getDate())}`;
    return dateString;
  }

  async getLocationRoutes(date) {
    this.setState({ isLoading: true });
    try {
      await GetLocationRoutes({
        stockingDate: this.formatDateObject(date),
        calculateTotes: true,
        includeOnlineDate: true,
      }).then((data) => {
        this.setState({ locationRoutes: data });
        // there's a data duplication: raw and sorted, decided to keep both in state
        const sortedData = this.sortRoutesData(data.routes);
        this.setState({ sortedData });
        // keptData is initial sorted data after query before filtering
        this.setState({ keptData: sortedData });
        this.filterByMarket(this.state.selectedMarket);
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  onDateChange(date) {
    this.setState({ date });
    this.getLocationRoutes(date);
  }

  filterByMarket(market) {
    if (market.value === 'ALL') {
      // fallback to initial data when market changed to ALL
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ displayData: this.state.keptData });
    } else {
      const removedNulls = this.state.sortedData.filter(function (el) {
        return el.market != null;
      });
      const filtered = removedNulls.filter(
        (route) => route.market.toUpperCase() === market.value,
      );
      // displayData is container to draw the table, sorted and filtered
      this.setState({ displayData: filtered });
    }
  }

  onMarketChange(market) {
    /*
          Takes market say "NE" from the dropdown and filters routs ids
          Caches selected market and sorted data on the first page load
          Updates sortedData to display the table
         */
    this.setState({ selectedMarket: market });
    this.filterByMarket(market);
  }

  sortRoutesData(items) {
    /*
        Takes items (list of dictionaries with routes data) as input
        Compares items to sort them corresponding to the route
        and if the names are of the same route compares stop number
        Example: for routes, a = 5 b = 1 if return b, a
                if rout names are equal places items asc by stop
        return: sorted routes data
    */
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      if (a.route < b.route) {
        return -1;
      }
      if (a.route > b.route) {
        return 1;
      }
      return a.stop - b.stop;
    });
    return sortableItems;
  }

  render() {
    return (
      <StyledDiv>
        <div>
          <div className="mainHeading">View Routes By Date</div>
        </div>
        <div className="wrap">
          <div className="subHeading">Select Date</div>
          <div className="subHeading">Select Region</div>
        </div>
        <div className="wrap">
          <div id="DatePicker">
            <DatePicker
              onChange={(date) => this.onDateChange(date)}
              value={this.state.date}
              format="MM/dd/y"
            />
          </div>
          <div id="MarketPicker">
            <Select
              options={this.state.markets}
              onChange={(market) => this.onMarketChange(market)}
              value={this.state.selectedMarket}
            />
          </div>
        </div>
        <div>
          {this.state.isLoading ? (
            <div id="Loading">
              <Loader />
            </div>
          ) : this.state.locationRoutes.routes &&
            this.state.locationRoutes.routes.length > 0 ? (
            <table id="RouteTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Region</th>
                  <th>Stop</th>
                  <th>Location</th>
                  <th>Tote Count</th>
                  <th>Stop Type</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const groupedRoutes = {};

                  this.state.displayData.forEach((route) => {
                    if (!groupedRoutes[route.route]) {
                      groupedRoutes[route.route] = [];
                    }
                    groupedRoutes[route.route].push(route);
                  });

                  return Object.keys(groupedRoutes).map((routeKey) => {
                    const routeData = groupedRoutes[routeKey];
                    console.log('routeData', routeData);

                    const totalStops = routeData.length;

                    return (
                      <>
                        <tr
                          key={`header-${routeKey}`}
                          className="header"
                          style={{
                            background: getRouteColor(routeKey),
                            fontWeight: 'bold',
                          }}
                        >
                          <td colSpan="6">ROUTE: {routeKey}</td>
                        </tr>
                        {routeData.map((route) => {
                          const twelveWeeksAfterOnlineDate = moment(
                            route.onlineDate,
                          )
                            .add(12, 'w')
                            .startOf('day');
                          const showPriority =
                            route.onlineDate &&
                            twelveWeeksAfterOnlineDate.isAfter(
                              moment().startOf('day'),
                            );
                          return (
                            <tr
                              key={`${route.route}-${route.stop}`}
                              style={{ background: getRouteColor(route.route) }}
                            >
                              <td>{route.stockingDate}</td>
                              <td className="alignCenter">{route.market}</td>
                              <td className="alignCenter">{route.stop}</td>
                              <td
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'space-between',
                                }}
                              >
                                {route.id}
                                {showPriority && (
                                  <StatusPill
                                    text="Priority: New Fridge"
                                    style={{ marginLeft: 'auto' }}
                                    width="200px"
                                    height="30px"
                                  />
                                )}
                              </td>
                              <td className="alignCenter">
                                {route.toteCountPerStop}
                              </td>
                              <td
                                style={{
                                  fontWeight:
                                    route.stopType === 'REMOVAL' ? 'bold' : '',
                                }}
                              >
                                {route.stopType}
                              </td>
                            </tr>
                          );
                        })}
                        <tr
                          key={`summary-${routeKey}`}
                          className="summary"
                          style={{
                            background: getRouteColor(routeKey),
                            fontWeight: 'bold',
                          }}
                        >
                          <td colSpan="2">SUMMARY</td>
                          <td className="alignCenter">{totalStops}</td>
                          <td />
                          <td className="alignCenter">
                            {routeData[0]?.toteCountPerRoute || ''}
                          </td>
                          <td />
                        </tr>
                      </>
                    );
                  });
                })()}
              </tbody>
            </table>
          ) : (
            <div id="EmptyRoutes">No routes available for given date</div>
          )}
        </div>
      </StyledDiv>
    );
  }
}
